'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PPUpcomingBooking } from '@micro-apps/env';
import { useForm } from '@fshr/hooks';
import { formatDateInLocale } from '@fshr/shared';
import { useI18n } from '@fshr/context/i18n-context';
import Alert from '../alert';
import BevSafeText from '../bev-safe-text';
import Button from '../button';
import Input from '../input';
import Select from '../select';
import TypeAhead, { type TypeAheadOption } from '../type-ahead';
import { IconAlert, IconCheckmarkFill, IconCheckmarkOutline } from '../icon';
import { BookingFoundDetails } from './booking-found-details';
import { BookingOptionDisplay } from './booking-option-display';
import type { BookingLookupFormProps, BookingLookupFormValues } from './types';

const BOOKING_DATE_FORMAT = 'yyyy-MM-dd';
const DETAIL_DATE_FORMAT = 'MMM d, yyyy';

function findBookingByConfirmation(
	bookings: PPUpcomingBooking[],
	confirmationNumber: string
): PPUpcomingBooking | undefined {
	if (!confirmationNumber) return undefined;
	return bookings.find(
		booking => booking.confirmationNumber.toLowerCase() === confirmationNumber.toLowerCase()
	);
}

function toFormValues(booking: PPUpcomingBooking): BookingLookupFormValues {
	return {
		confirmationNumber: booking.confirmationNumber,
		property: booking.propertyShortName || booking.propertyOrsCode,
		lastName: booking.lastName,
		checkInDate: booking.checkInDate,
		checkOutDate: booking.checkOutDate,
	};
}

function NoRecordsFound({ title, description }: { title: ReactNode; description: ReactNode }) {
	return (
		<div className="flex w-full flex-col gap-[4px] bg-surface-raised-subtle px-150 py-150 text-start shadow-[0px_5px_15px_0px_#0303031F]">
			<p className="text-body2 font-bold text-text-primary">
				<BevSafeText>{title}</BevSafeText>
			</p>
			<p className="text-body2 text-text-primary">
				<BevSafeText>{description}</BevSafeText>
			</p>
		</div>
	);
}

export default function BookingLookupForm({
	bookings,
	allowManualLookup = false,
	propertyOptions = [],
	onBack,
	onSubmit,
	submitButtonText,
	disableBack = false,
	errorMessage,
	onSelectedBookingChange,
	initialValues,
	noResultsTitle,
	noResultsDescription,
	showReadonlyOnSuccess = false,
}: BookingLookupFormProps) {
	const { t, locale } = useI18n();

	const restoredBooking = useMemo(
		() => findBookingByConfirmation(bookings, initialValues?.confirmationNumber || ''),
		[bookings, initialValues?.confirmationNumber]
	);

	const [selectedBooking, setSelectedBooking] = useState<PPUpcomingBooking | undefined>(
		restoredBooking
	);

	const confirmationOptions: TypeAheadOption[] = useMemo(
		() =>
			bookings.map(booking => ({
				label: booking.confirmationNumber,
				value: booking.confirmationNumber,
				display: (_currentValue, result) => (
					<BookingOptionDisplay booking={booking} result={result} />
				),
			})),
		[bookings]
	);

	const updateSelectedBooking = (booking: PPUpcomingBooking | undefined) => {
		setSelectedBooking(booking);
		onSelectedBookingChange?.(booking);
	};

	const handleFormSubmit = (formValues: Record<string, string>) => {
		const matchedBooking =
			selectedBooking ||
			findBookingByConfirmation(bookings, formValues['confirmationNumber'] || '');
		if (matchedBooking) {
			onSubmit(toFormValues(matchedBooking), matchedBooking);
			return;
		}

		onSubmit({
			confirmationNumber: formValues['confirmationNumber'] || '',
			property: formValues['property'] || '',
			lastName: formValues['lastName'] || '',
			checkInDate: formValues['checkInDate'] || '',
			checkOutDate: formValues['checkOutDate'] || '',
		});
	};

	const { handleSubmit, registerField, unregisterField } = useForm({
		onSubmit: handleFormSubmit,
	});

	const confirmationRequired = !allowManualLookup;
	const hasTechnicalError = Boolean(errorMessage);
	const showFoundState = Boolean(selectedBooking) && !hasTechnicalError;
	const showManualFields = allowManualLookup && !selectedBooking;
	const showConfirmationTypeahead =
		!allowManualLookup || !selectedBooking || hasTechnicalError || !showReadonlyOnSuccess;

	useEffect(() => {
		if (!allowManualLookup || !selectedBooking) return;
		unregisterField({ name: 'property' });
		unregisterField({ name: 'lastName' });
		unregisterField({ name: 'checkInDate' });
		unregisterField({ name: 'checkOutDate' });
	}, [allowManualLookup, selectedBooking, unregisterField]);

	const formatDetailDate = (dateStr: string) =>
		formatDateInLocale(dateStr, DETAIL_DATE_FORMAT, BOOKING_DATE_FORMAT, locale);

	const confirmationNoResults = (currentValue: string) => (
		<NoRecordsFound title={noResultsTitle(currentValue)} description={noResultsDescription} />
	);

	const confirmationInitialValue =
		restoredBooking?.confirmationNumber || initialValues?.confirmationNumber || '';

	const confirmationField = registerField({
		name: 'confirmationNumber',
		initialValue: confirmationInitialValue,
		type: 'typeahead',
		options: {
			required: confirmationRequired,
			errorMessages: {
				empty: t('Confirmation Number is required.'),
				partial: t('Please select a valid confirmation number.'),
			},
			onChange: event => {
				updateSelectedBooking(
					findBookingByConfirmation(bookings, event?.currentTarget?.value || '')
				);
			},
		},
		validate: confirmationRequired
			? (value?: string) => {
					if (hasTechnicalError) {
						return [true, '', ''];
					}
					const found = findBookingByConfirmation(bookings, value || '');
					if (!value) {
						return [false, t('Confirmation Number is required.'), 'empty'];
					}
					if (!found) {
						return [false, t('Please select a valid confirmation number.'), 'partial'];
					}
					return [true, '', ''];
				}
			: undefined,
	});

	const confirmationValue =
		confirmationField.value !== undefined && confirmationField.value !== null
			? confirmationField.value
			: confirmationInitialValue;

	const handleConfirmationTypeaheadChange = event => {
		if (!allowManualLookup) {
			confirmationField.onChange(event);
			return;
		}
		const typed = event?.data?.displayValue ?? event?.currentTarget?.value ?? '';
		const matched = findBookingByConfirmation(bookings, typed);
		confirmationField.onChange({
			...event,
			currentTarget: {
				...event.currentTarget,
				value: matched?.confirmationNumber || typed,
			},
		});
	};

	return (
		<form noValidate className="flex w-full flex-col spacing-stack-l" onSubmit={handleSubmit}>
			<div className="flex min-h-[375px] w-full flex-col">
				{showConfirmationTypeahead && (
					<TypeAhead
						id="confirmationNumber"
						label={t('Confirmation Number')}
						placeholder={t('Confirmation Number')}
						options={confirmationOptions}
						noResults={confirmationNoResults}
						resultButtonWrapperClassName="w-full border-b border-border-subtle bg-surface-raised-subtle px-150 py-150"
						{...confirmationField}
						onChange={handleConfirmationTypeaheadChange}
						value={confirmationValue}
					/>
				)}

				{allowManualLookup && showFoundState && showReadonlyOnSuccess && (
					<Input
						id="confirmationNumberReadOnly"
						label={t('Confirmation Number')}
						value={selectedBooking?.confirmationNumber || ''}
						className="pb-[25px]"
						readOnly
						required
					/>
				)}

				{hasTechnicalError && !showManualFields && (
					<Alert intent="error" kind="subtle" icon={<IconAlert fill="currentColor" />}>
						<span className="text-caption">
							{errorMessage ||
								t('We could not retrieve your reservation at this time. Please try again later.')}
						</span>
					</Alert>
				)}

				{showFoundState && (
					<div className="flex w-full flex-col spacing-stack-m">
						<Alert
							intent="success"
							kind="subtle"
							icon={
								<IconCheckmarkOutline
									width={20}
									height={20}
									className="block size-[20px] shrink-0"
								/>
							}
						>
							<span className="text-caption">
								{t(
									'Reservation found. Please review the details below and click Next to continue.'
								)}
							</span>
						</Alert>
						<BookingFoundDetails
							property={
								selectedBooking?.propertyShortName || selectedBooking?.propertyOrsCode || ''
							}
							lastName={selectedBooking?.lastName || ''}
							checkInDate={
								selectedBooking?.checkInDate ? formatDetailDate(selectedBooking.checkInDate) : ''
							}
							checkOutDate={
								selectedBooking?.checkOutDate ? formatDetailDate(selectedBooking.checkOutDate) : ''
							}
						/>
					</div>
				)}

				{showManualFields && (
					<div className="flex flex-col spacing-stack-m">
						{hasTechnicalError && (
							<Alert intent="error" kind="subtle" icon={<IconAlert fill="currentColor" />}>
								<span className="text-caption">
									{errorMessage ||
										t(
											'We could not retrieve your reservation at this time. Please try again later.'
										)}
								</span>
							</Alert>
						)}
						{showFoundState && (
							<Alert
								intent="success"
								kind="subtle"
								icon={<IconCheckmarkFill width={20} height={20} fill="currentColor" />}
							>
								<span className="text-caption">
									{t(
										'Reservation found. Please review the details below and click Next to continue.'
									)}
								</span>
							</Alert>
						)}
						<div className="grid grid-cols-1 lg:grid-cols-2 spacing-grid-gutter lg:!gap-y-[0px]">
							<Select
								id="property"
								label={t('Property')}
								placeholder={t('Property')}
								fullWidth
								options={propertyOptions}
								className="h-[47px] bg-surface-form"
								selectClassName="text-body2 focus:outline-hidden"
								{...registerField({
									name: 'property',
									initialValue: initialValues?.property || '',
									options: {
										required: true,
										errorMessages: { empty: t('Property is required.') },
									},
								})}
							/>
							<Input
								id="lastName"
								label={t('Last Name')}
								placeholder={t('Last Name')}
								{...registerField({
									name: 'lastName',
									initialValue: initialValues?.lastName || '',
									options: {
										required: true,
										errorMessages: { empty: t('Last Name is required.') },
									},
								})}
							/>
							<Input
								id="checkInDate"
								type="date"
								label={t('Check In Date')}
								placeholder={t('Check In Date')}
								{...registerField({
									name: 'checkInDate',
									initialValue: initialValues?.checkInDate || '',
									options: {
										required: true,
										errorMessages: { empty: t('Check In date is required.') },
									},
								})}
							/>
							<Input
								id="checkOutDate"
								type="date"
								label={t('Check Out Date')}
								placeholder={t('Check Out Date')}
								{...registerField({
									name: 'checkOutDate',
									initialValue: initialValues?.checkOutDate || '',
									options: {
										required: true,
										errorMessages: { empty: t('Check Out date is required.') },
									},
								})}
							/>
						</div>
					</div>
				)}
			</div>

			<div className="flex w-full flex-row justify-center md:justify-end gap-[10px] [&>*]:w-1/2 md:[&>*]:w-[130px]">
				<Button
					type="button"
					intent="outline"
					className="spacing-button-inline"
					disabled={disableBack}
					onClick={onBack}
				>
					{t('Back')}
				</Button>
				<Button type="submit" intent="filled" disabled={hasTechnicalError && !allowManualLookup}>
					{submitButtonText ?? t('Submit')}
				</Button>
			</div>
		</form>
	);
}
